import { forwardRef, useEffect, useState } from 'react'
import {
    Toolbar,
    Grid,
    Dialog,
    Slide,
    Typography,
    Divider,
    Box,
    Card,
    CardContent,
    IconButton,
    TextField,
    Button,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material'
import { Close, Refresh, SearchOff } from '@mui/icons-material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChartLine, faClipboardCheck, faExclamationTriangle, faClock } from '@fortawesome/free-solid-svg-icons'
import ReportsService from '../services/reports.service'
import { useReportsContext, useSitesContext } from '../context'

const Transition = forwardRef(function Transition (props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
})

const KPICard = ({ title, value, subtitle, icon, color }) => (
    <Card sx={{ height: '100%', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)', borderRadius: 3 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Grid container spacing={1} alignItems="center">
                <Grid item>
                    <Box sx={{
                        backgroundColor: `${color}15`,
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <FontAwesomeIcon icon={icon} style={{ color: color, fontSize: 18 }} />
                    </Box>
                </Grid>
                <Grid item xs>
                    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                        {title}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                            {subtitle}
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </CardContent>
    </Card>
)

const MetricRow = ({ label, count, avg }) => (
    <Grid container sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' }, '&:hover': { backgroundColor: '#fafafa' } }}>
        <Grid item xs={6} md={5}>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{label}</Typography>
        </Grid>
        <Grid item xs={3} md={3} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>{count} items</Typography>
        </Grid>
        <Grid item xs={3} md={4} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: avg > 5 ? '#d32f2f' : '#2e7d32' }}>
                {avg.toFixed(2)} días
            </Typography>
        </Grid>
    </Grid>
)

const ReportsKPIDialog = ({ open, handleClose }) => {
    const { siteSelected } = useReportsContext()
    const { sites } = useSitesContext()

    const [filterStart, setFilterStart] = useState('')
    const [filterEnd, setFilterEnd] = useState('')
    const [filterSite, setFilterSite] = useState('all')
    const [filterType, setFilterType] = useState('todos')
    const [isLoading, setIsLoading] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)
    const [stats, setStats] = useState({
        total: 0,
        inspecciones: 0,
        mantenciones: 0,
        hallazgos: 0,
        finalizados: 0,
        dev: { count: 0, avg: 0 },
        sup: { count: 0, avg: 0 },
        chief: { count: 0, avg: 0 },
        close: { count: 0, avg: 0 }
    })

    const fetchKPIs = async () => {
        setIsLoading(true)
        setHasSearched(true)
        try {
            const data = await ReportsService.getKPIs({
                startDate: filterStart,
                endDate: filterEnd,
                site: filterSite,
                reportType: filterType
            })

            setStats({
                total: data.total,
                inspecciones: data.inspecciones,
                mantenciones: data.mantenciones,
                hallazgos: data.hallazgos,
                finalizados: data.finalizados,
                dev: data.dev,
                sup: data.sup,
                chief: data.chief,
                close: data.close
            })
        } catch (error) {
            console.error('Error fetching KPIs:', error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (open) {
            // Set current week range (Monday to Friday)
            const today = new Date()
            const day = today.getDay()
            const diffToMon = today.getDate() - day + (day === 0 ? -6 : 1)
            const monday = new Date(today.setDate(diffToMon))
            const friday = new Date(today.setDate(diffToMon + 4))

            setFilterStart(monday.toISOString().split('T')[0])
            setFilterEnd(friday.toISOString().split('T')[0])

            // Set initial site from global context
            const initialSite = (siteSelected && siteSelected.idObra !== 'none') ? siteSelected.idObra : 'all'
            setFilterSite(initialSite)

            // Reset search state
            setHasSearched(false)
            setStats({
                total: 0,
                inspecciones: 0,
                mantenciones: 0,
                hallazgos: 0,
                finalizados: 0,
                dev: { count: 0, avg: 0 },
                sup: { count: 0, avg: 0 },
                chief: { count: 0, avg: 0 },
                close: { count: 0, avg: 0 }
            })
        }
    }, [open, siteSelected])

    const isValidRange = !filterStart || !filterEnd || (new Date(filterStart) <= new Date(filterEnd))

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
        >
            <Toolbar sx={{ backgroundColor: '#F9F9F9', borderBottom: '1px solid #ddd', py: 1 }}>
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 'bold', color: '#333' }}>
                    Indicadores de Gestión (KPI)
                </Typography>

                <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
                    <Close />
                </IconButton>
            </Toolbar>

            <Box sx={{ p: 3, backgroundColor: '#fcfcfc' }}>
                {/* Filters Row */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'flex-end', justifyContent: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Sitio / Obra</InputLabel>
                        <Select
                            value={filterSite}
                            label="Sitio / Obra"
                            onChange={(e) => setFilterSite(e.target.value)}
                        >
                            <MenuItem value="all">Todas las Obras</MenuItem>
                            {(sites || []).filter(s => s.status).map(site => (
                                <MenuItem key={site._id} value={site.idObra}>
                                    {site.descripcion}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Tipo Reporte</InputLabel>
                        <Select
                            value={filterType}
                            label="Tipo Reporte"
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <MenuItem value="todos">Todos</MenuItem>
                            <MenuItem value="Inspección">Inspección</MenuItem>
                            <MenuItem value="Mantención">Mantención</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Desde"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={filterStart}
                        onChange={(e) => setFilterStart(e.target.value)}
                        sx={{ width: 160 }}
                    />
                    <TextField
                        label="Hasta"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={filterEnd}
                        onChange={(e) => setFilterEnd(e.target.value)}
                        sx={{ width: 160 }}
                        error={!isValidRange}
                    />
                    <Button
                        variant="contained"
                        onClick={fetchKPIs}
                        startIcon={<Refresh />}
                        disabled={isLoading || !isValidRange}
                        sx={{ height: 40 }}
                    >
                        Actualizar
                    </Button>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {isLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                        <CircularProgress size={50} thickness={4} />
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                            Espere un momento...
                        </Typography>
                    </Box>
                ) : !hasSearched ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, opacity: 0.7 }}>
                        <FontAwesomeIcon icon={faChartLine} style={{ fontSize: 80, color: '#ccc' }} />
                        <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
                            Panel de Estadísticas
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Seleccione el rango de fechas y presione <strong>Actualizar</strong> para ver los datos.
                        </Typography>
                    </Box>
                ) : stats.total === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, opacity: 0.7 }}>
                        <SearchOff sx={{ fontSize: 80, color: '#ccc' }} />
                        <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
                            No se encontraron datos
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Intente ajustar los filtros de fecha o tipo de reporte.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {/* General Summary Cards */}
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Total Reportes"
                                value={stats.total}
                                subtitle={`${stats.inspecciones} Insp. / ${stats.mantenciones} Mant.`}
                                icon={faChartLine}
                                color="#1976d2"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Total Hallazgos"
                                value={stats.hallazgos}
                                subtitle="Encontrados en pautas"
                                icon={faExclamationTriangle}
                                color="#ed6c02"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Eficiencia Ciclo"
                                value={`${(stats.dev.avg + stats.sup.avg + stats.chief.avg + stats.close.avg).toFixed(1)}`}
                                subtitle="Días promedio total"
                                icon={faClock}
                                color="#2e7d32"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KPICard
                                title="Finalizados"
                                value={stats.finalizados || 0}
                                subtitle="Cerrados administrativamente"
                                icon={faClipboardCheck}
                                color="#9c27b0"
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Promedio de días por etapa</Typography>
                            <Card sx={{ boxShadow: '0 4px 12px 0 rgba(0,0,0,0.05)', borderRadius: 3 }}>
                                <CardContent sx={{ p: 0 }}>
                                    <Grid container sx={{ p: 1.5, backgroundColor: '#f5f5f5', borderBottom: '1px solid #eee' }}>
                                        <Grid item xs={6} md={5}><Typography variant="caption" sx={{ fontWeight: 'bold' }}>ETAPA DEL FLUJO</Typography></Grid>
                                        <Grid item xs={3} md={3} sx={{ textAlign: 'center' }}><Typography variant="caption" sx={{ fontWeight: 'bold' }}>MUESTRA</Typography></Grid>
                                        <Grid item xs={3} md={4} sx={{ textAlign: 'center' }}><Typography variant="caption" sx={{ fontWeight: 'bold' }}>PROMEDIO</Typography></Grid>
                                    </Grid>

                                    <MetricRow
                                        label="Ejecución Operativa"
                                        count={stats.dev.count}
                                        avg={stats.dev.avg}
                                    />
                                    <MetricRow
                                        label="Revisión Supervisor"
                                        count={stats.sup.count}
                                        avg={stats.sup.avg}
                                    />
                                    <MetricRow
                                        label="Revisión Maquinaria"
                                        count={stats.chief.count}
                                        avg={stats.chief.avg}
                                    />
                                    <MetricRow
                                        label="Cierre SAP"
                                        count={stats.close.count}
                                        avg={stats.close.avg}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </Box>
        </Dialog>
    )
}

export default ReportsKPIDialog
